import { Heading } from '@/components/heading';
import { Link } from '@/components/link';
import { Text } from '@/components/text';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

export interface DashboardCardProps {
  title: string;
  description?: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  ctaHref?: string;
  ctaText?: string;
  isSkinless?: boolean;
  heightConstraint?: boolean;
}

const DashboardCard = ({
  title,
  description,
  icon,
  children,
  ctaHref,
  ctaText = 'See all',
  isSkinless = false,
  heightConstraint = true,
}: DashboardCardProps) => {
  const { card, cardHeading, cardHeadingText, cardDescription, cardBody, cardFooter, transitionStrip } = styles({
    hasMaxHeight: heightConstraint,
    isSkinless,
  });

  const handleCtaClick = () => {
    trackUser.event(`Clicked Dashboard Card CTA`, { label: ctaText });
  };

  return (
    <article className={card()}>
      <header className={cardHeading()}>
        <span className={cardHeadingText()}>
          {icon}
          <Heading variant="h4" className="mb-0">
            {title}
          </Heading>
        </span>
        {description && <Text className={cardDescription()}>{description}</Text>}
      </header>
      <div className={cardBody()}>
        <div>{children}</div>
      </div>
      {ctaHref && (
        <footer className={cardFooter()}>
          <span className={transitionStrip()} />
          <Link onClick={handleCtaClick} href={ctaHref}>
            {ctaText}
          </Link>
        </footer>
      )}
    </article>
  );
};

const styles = tv({
  slots: {
    card: 'flex flex-col rounded-md bg-white p-4 shadow',
    cardHeading: 'mb-2 border-b border-grey-200 pb-4',
    cardHeadingText: 'flex items-center [&_svg]:mr-2 [&_svg]:size-5 [&_svg]:text-black',
    cardDescription: 'mb-0 mt-0.5 max-w-[500px] text-xs leading-snug text-text-secondary',
    cardBody: 'relative flex-1 overflow-auto text-baseSm',
    cardFooter: 'relative -mb-1.5 pt-1.5 text-center text-baseSm text-lightBlue-500 hover:[&_a]:underline',
    transitionStrip: 'absolute -top-4 left-0 right-0 hidden h-4 bg-gradient-to-t from-white to-transparent md:block',
  },
  variants: {
    hasMaxHeight: {
      true: {
        card: 'min-h-lg md:h-[500px]',
      },
      false: {},
    },
    isSkinless: {
      true: {
        card: 'rounded-none bg-transparent p-0 shadow-none',
        cardBody: '-mx-2',
      },
      false: {},
    },
  },
});

export { DashboardCard };
